import TableQueryFooter from './TableQueryFooter'
import { Select } from '@/components/select/Select'
import { getColumnIcon } from './column-icons'
import Sort from '@/assets/icons/table-sort.svg?react'
import { useEffect, useRef, useState } from 'react'
import { TableColumn, TableQuery } from '../schemas'
import { Button } from '@/components/button/Button'
import { OverflowMenu, XClose } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'

export default function TableSort({
  disabled = false,
  columns,
  sorts,
  onChange,
  onOpen,
  emptyLabel = 'Newest entries first.',
}: {
  disabled?: boolean
  columns: (Pick<TableColumn, 'id' | 'name' | 'kind' | 'source_file'> & Partial<Pick<TableColumn, 'allow_multiple'>>)[]
  emptyLabel?: string
  sorts: TableQuery['sorts']
  onChange: (sorts: TableQuery['sorts']) => void
  onOpen?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<{ column_id: string; descending: boolean | null }[]>(sorts)
  const draggedColumn = useRef<string | null>(null)
  const dragPreview = useRef<HTMLDivElement | null>(null)
  useEffect(() => () => dragPreview.current?.remove(), [])
  const moveSort = (from: number, to: number) => {
    if (from < 0 || to < 0 || to >= draft.length || from === to) return
    const next = [...draft]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    setDraft(next)
  }
  const available = columns.filter((column) => !draft.some((sort) => sort.column_id === column.id))
  const first = sorts[0]
  const label = !first
    ? 'Sort'
    : sorts.length > 1
    ? `Sort · ${sorts.length}`
    : `Sort · ${columns.find((column) => column.id === first.column_id)?.name ?? 'Column'} ${first.descending ? '↓' : '↑'}`
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          onOpen?.()
          setDraft(sorts.length || !columns.length ? sorts : [{ column_id: columns[0].id, descending: null }])
        }
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <IconButton
          disabled={disabled}
          size="sm"
          variant="plain"
          color={sorts.length ? 'primary' : 'black'}
          icon={
            <span className="relative">
              <Sort className="h-4 w-4" />
              {sorts.length > 0 && <span className="absolute -right-[3.5px] -top-[3.5px] h-1.5 w-1.5 rounded-full bg-current" />}
            </span>
          }
          className={`disabled:!text-true-gray-200 ${sorts.length ? 'bg-brand-50' : ''}`}
          aria-label={label}
          aria-pressed={sorts.length > 0}
          tooltipText={sorts.length ? `Sort · ${sorts.length}` : 'Sort'}
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        width="auto"
        showArrow={false}
        className="w-[560px] max-w-[calc(100vw-32px)] border border-brand-neutral-200 p-4 shadow-menu"
        aria-label="Table sorting"
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            const completeSorts: TableQuery['sorts'] = []
            for (const sort of draft) {
              if (sort.descending === null) return
              completeSorts.push({ column_id: sort.column_id, descending: sort.descending })
            }
            onChange(completeSorts)
            setOpen(false)
          }}
        >
          <span className="text-lg font-medium leading-6">Sort</span>
          <div className="-m-1 flex max-h-[50vh] flex-col gap-3 overflow-y-auto p-1">
            {!draft.length && <p className="text-sm text-brand-neutral-600">{emptyLabel}</p>}
            {draft.map((item, index) => {
              const column = columns.find((column) => column.id === item.column_id)
              const KindIcon = getColumnIcon(column?.kind, column?.allow_multiple, column?.source_file)
              return (
                <div
                  key={item.column_id}
                  className="flex items-center gap-2.5"
                  onDragOver={(event) => {
                    if (draggedColumn.current) {
                      event.preventDefault()
                      event.dataTransfer.dropEffect = 'move'
                    }
                  }}
                  onDrop={(event) => {
                    if (!draggedColumn.current) return
                    event.preventDefault()
                    moveSort(
                      draft.findIndex((sort) => sort.column_id === draggedColumn.current),
                      index
                    )
                    draggedColumn.current = null
                  }}
                >
                  <button
                    type="button"
                    draggable
                    className="flex h-8 w-6 shrink-0 cursor-grab items-center justify-center rounded text-brand-neutral-600 active:cursor-grabbing focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
                    aria-label={`Reorder sort ${index + 1}: ${column?.name ?? 'Column'}`}
                    title="Drag to reorder, or use the up and down arrow keys"
                    onDragStart={(event) => {
                      draggedColumn.current = item.column_id
                      event.dataTransfer.effectAllowed = 'move'
                      event.dataTransfer.setData('text/plain', item.column_id)
                      dragPreview.current?.remove()
                      const row = event.currentTarget.parentElement
                      if (!row) return
                      const bounds = row.getBoundingClientRect()
                      const preview = row.cloneNode(true) as HTMLDivElement
                      preview.className =
                        'pointer-events-none fixed -left-[10000px] top-0 flex items-center gap-2.5 rounded-md border border-brand-neutral-300 bg-white p-2 shadow-menu'
                      preview.style.width = `${bounds.width + 18}px`
                      preview.setAttribute('aria-hidden', 'true')
                      preview.inert = true
                      preview.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'))
                      document.body.append(preview)
                      dragPreview.current = preview
                      event.dataTransfer.setDragImage(preview, event.clientX - bounds.left + 9, event.clientY - bounds.top + 9)
                    }}
                    onDragEnd={() => {
                      dragPreview.current?.remove()
                      dragPreview.current = null
                      draggedColumn.current = null
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                        event.preventDefault()
                        moveSort(index, index + (event.key === 'ArrowUp' ? -1 : 1))
                      }
                    }}
                  >
                    <OverflowMenu aria-hidden className="h-4 w-4" />
                  </button>
                  <div className="relative min-w-0 flex-1">
                    <Select
                      ariaLabel={`Sort column ${index + 1}`}
                      triggerClassName="text-black [&>div>span]:font-normal"
                      className="[&_[role=option]>div>span]:font-normal [&_[role=option]]:text-black"
                      leadingIcon={<KindIcon className="h-4 w-4 text-brand-neutral-600" />}
                      selectedItemId={item.column_id}
                      onSelect={(value) => setDraft(draft.map((sort, i) => (i === index ? { ...sort, column_id: value } : sort)))}
                      items={columns
                        .filter((col) => col.id === item.column_id || !draft.some((sort) => sort.column_id === col.id))
                        .map((col) => {
                          const Icon = getColumnIcon(col.kind, col.allow_multiple, col.source_file)
                          return { id: col.id, label: col.name, leadingIcon: <Icon className="h-4 w-4 shrink-0 text-brand-neutral-600" /> }
                        })}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Select
                      ariaLabel={`Sort direction ${index + 1}`}
                      triggerClassName={`${item.descending === null ? 'text-brand-neutral-600' : 'text-black'} [&>div>span]:font-normal`}
                      className="[&_[role=option]>div>span]:font-normal [&_[role=option]]:text-black"
                      placeholder="Select sort order"
                      selectedItemId={item.descending === null ? undefined : String(item.descending)}
                      onSelect={(value) => setDraft(draft.map((sort, i) => (i === index ? { ...sort, descending: value === 'true' } : sort)))}
                      items={[
                        { id: 'false', label: 'Ascending' },
                        { id: 'true', label: 'Descending' },
                      ]}
                    />
                  </div>
                  <IconButton
                    type="button"
                    size="sm"
                    variant="plain"
                    color="neutral"
                    icon={<XClose />}
                    aria-label={`Remove sort ${index + 1}`}
                    onClick={() => setDraft(draft.filter((_, i) => i !== index))}
                  />
                </div>
              )
            })}
          </div>
          <div className="flex h-10 items-center">
            <Button
              type="button"
              size="sm"
              color="secondary"
              variant="plain"
              className="!font-medium enabled:!text-black"
              disabled={!available.length}
              onClick={() => setDraft([...draft, { column_id: available[0].id, descending: null }])}
            >
              + Add sort
            </Button>
          </div>
          <TableQueryFooter
            applyDisabled={draft.some((sort) => sort.descending === null)}
            onClear={() => {
              setDraft([])
              onChange([])
              setOpen(false)
            }}
            onCancel={() => setOpen(false)}
          />
        </form>
      </PopoverContent>
    </Popover>
  )
}
